/**
 * The assistant's answers come as light Markdown (bold leads, bullet lists,
 * the odd table of figures). Rendered through react-markdown with GitHub
 * tables, mapped onto MUI typography so it reads like the rest of the page.
 * No raw HTML is ever rendered: react-markdown escapes it by default.
 */
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const components: Components = {
  p: ({ children }) => (
    <Typography variant="body2" sx={{ my: 0.75, "&:first-of-type": { mt: 0 } }}>
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1.25, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ my: 0.5, pl: 2.5, "& li": { my: 0.25 } }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ my: 0.5, pl: 2.5, "& li": { my: 0.25 } }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body2">
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700 }}>
      {children}
    </Box>
  ),
  code: ({ children }) => (
    <Box
      component="code"
      className="num"
      sx={{
        px: 0.5,
        borderRadius: 0.5,
        bgcolor: "action.hover",
        fontSize: "0.9em",
        wordBreak: "break-all",
      }}
    >
      {children}
    </Box>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        my: 1,
        p: 1,
        borderRadius: 1,
        bgcolor: "action.hover",
        overflowX: "auto",
        fontSize: "0.85em",
        "& code": { bgcolor: "transparent", px: 0 },
      }}
    >
      {children}
    </Box>
  ),
  a: ({ children, href }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  ),
  blockquote: ({ children }) => (
    <Box sx={{ my: 1, pl: 1.5, borderLeft: 3, borderColor: "divider", color: "text.secondary" }}>
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <TableContainer sx={{ my: 1 }}>
      <Table size="small">{children}</Table>
    </TableContainer>
  ),
  thead: ({ children }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => (
    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{children}</TableCell>
  ),
  td: ({ children }) => <TableCell>{children}</TableCell>,
  hr: () => <Box component="hr" sx={{ my: 1, border: 0, borderTop: 1, borderColor: "divider" }} />,
};

export function Markdown({ text }: { text: string }) {
  return (
    <Box sx={{ overflowWrap: "anywhere" }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </Box>
  );
}
